import { useState } from "react";
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, Music, Shirt, Car, Users, DollarSign, Package, 
  Plus, Trash2, X, Save, Wand2, Loader2, Sparkles, Download,
  Tag, BarChart3, TrendingUp, TrendingDown, Calendar, Percent, Hash, AlertTriangle,
  Mail, Send, Mic2, Volume2, FileText, MessageCircle, Link2, Upload, ShoppingCart, FlaskConical, Layers, Trophy, Star, Gift, Zap, CheckCircle, XCircle, Crown, ImageIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OverviewTab = ({ stats }) => (
  <>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
      <div className="glass p-6">
        <Music className="w-8 h-8 text-neon-cyan mb-3" />
        <p className="text-3xl font-heading text-neon-cyan">{stats?.total_beats || 0}</p>
        <p className="text-sm text-zinc-400 uppercase tracking-wide">Beats</p>
      </div>
      <div className="glass p-6">
        <Shirt className="w-8 h-8 text-neon-magenta mb-3" />
        <p className="text-3xl font-heading text-neon-magenta">{stats?.total_merch || 0}</p>
        <p className="text-sm text-zinc-400 uppercase tracking-wide">Merch</p>
      </div>
      <div className="glass p-6">
        <Package className="w-8 h-8 text-neon-green mb-3" />
        <p className="text-3xl font-heading text-neon-green">{stats?.total_orders || 0}</p>
        <p className="text-sm text-zinc-400 uppercase tracking-wide">Orders</p>
      </div>
      <div className="glass p-6">
        <Users className="w-8 h-8 text-neon-yellow mb-3" />
        <p className="text-3xl font-heading text-neon-yellow">{stats?.total_users || 0}</p>
        <p className="text-sm text-zinc-400 uppercase tracking-wide">Users</p>
      </div>
      <div className="glass p-6">
        <DollarSign className="w-8 h-8 text-white mb-3" />
        <p className="text-3xl font-heading">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
        <p className="text-sm text-zinc-400 uppercase tracking-wide">Revenue</p>
      </div>
    </div>
    <div className="glass p-6">
      <h3 className="font-heading text-xl uppercase tracking-wide mb-4">Recent Orders</h3>
      {stats?.recent_orders?.length > 0 ? (
        <div className="space-y-3">
          {stats.recent_orders.map((order) => (
            <div key={order.order_id} className="flex items-center justify-between p-4 bg-white/5 rounded-sm">
              <div>
                <p className="font-mono text-sm">{order.order_id}</p>
                <p className="text-xs text-zinc-500">{order.items?.length || 0} items</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-neon-cyan">${order.total?.toFixed(2)}</p>
                <Badge variant="outline" className="text-xs">{order.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-zinc-500 text-center py-8">No orders yet</p>
      )}
    </div>
  </>
);

const AnalyticsTab = ({ analytics }) => {
  if (!analytics) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-neon-yellow" />
      </div>
    );
  }

  const { summary, chart_data, top_products } = analytics;
  const TrendIcon = summary.revenue_trend >= 0 ? TrendingUp : TrendingDown;
  const trendColor = summary.revenue_trend >= 0 ? "text-neon-green" : "text-red-500";

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass p-6">
          <DollarSign className="w-6 h-6 text-neon-cyan mb-2" />
          <p className="text-2xl font-heading text-neon-cyan">${summary.total_revenue.toFixed(2)}</p>
          <p className="text-xs text-zinc-400 uppercase">Total Revenue</p>
          <div className={`flex items-center gap-1 mt-2 text-xs ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            <span>{Math.abs(summary.revenue_trend)}% vs prev period</span>
          </div>
        </div>
        <div className="glass p-6">
          <Package className="w-6 h-6 text-neon-magenta mb-2" />
          <p className="text-2xl font-heading text-neon-magenta">{summary.total_orders}</p>
          <p className="text-xs text-zinc-400 uppercase">Total Orders</p>
        </div>
        <div className="glass p-6">
          <BarChart3 className="w-6 h-6 text-neon-green mb-2" />
          <p className="text-2xl font-heading text-neon-green">${summary.avg_order_value.toFixed(2)}</p>
          <p className="text-xs text-zinc-400 uppercase">Avg Order Value</p>
        </div>
        <div className="glass p-6">
          <Tag className="w-6 h-6 text-neon-yellow mb-2" />
          <p className="text-2xl font-heading text-neon-yellow">${summary.total_discount_given.toFixed(2)}</p>
          <p className="text-xs text-zinc-400 uppercase">Discounts Given</p>
          <p className="text-xs text-zinc-500 mt-1">{summary.promo_usage_count} promo uses</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="glass p-6">
        <h3 className="font-heading text-lg uppercase mb-4">Revenue (Last 30 Days)</h3>
        <div className="h-64" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart_data}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00f3ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00f3ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="date"
                stroke="#71717a"
                fontSize={11}
                tickFormatter={(v) => v.slice(5)}
              />
              <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{
                  background: "#09090b",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "2px",
                  color: "#fff",
                  fontSize: "12px"
                }}
                formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]}
                labelFormatter={(l) => `Date: ${l}`}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#00f3ff"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders Chart */}
      <div className="glass p-6">
        <h3 className="font-heading text-lg uppercase mb-4">Orders (Last 30 Days)</h3>
        <div className="h-48" data-testid="orders-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: "#09090b",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "2px",
                  color: "#fff",
                  fontSize: "12px"
                }}
                formatter={(v) => [v, "Orders"]}
                labelFormatter={(l) => `Date: ${l}`}
              />
              <Bar dataKey="orders" fill="#ff00ff" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Products */}
      <div className="glass p-6">
        <h3 className="font-heading text-lg uppercase mb-4">Top Selling Products</h3>
        {top_products.length > 0 ? (
          <div className="space-y-3">
            {top_products.map((product, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-white/5 rounded-sm">
                <span className="w-8 h-8 flex items-center justify-center bg-zinc-800 font-mono text-sm text-neon-cyan">
                  #{i + 1}
                </span>
                {product.image && (
                  <img src={product.image} alt={product.name} className="w-10 h-10 object-cover rounded-sm" />
                )}
                <div className="flex-1">
                  <p className="font-medium">{product.name}</p>
                  <Badge variant="outline" className="text-xs">{product.type}</Badge>
                </div>
                <p className="font-mono text-neon-green">{product.sales} sold</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500 text-center py-8">No sales data yet. Sales data appears here after orders are completed.</p>
        )}
      </div>
    </div>
  );
};

const PromoCodesTab = ({
  promoCodes, showPromoDialog, setShowPromoDialog,
  promoForm, setPromoForm, handleCreatePromo, handleTogglePromo
}) => (
  <div data-testid="promo-codes-manager">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Discount Codes</h2>
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogTrigger asChild>
          <Button className="bg-neon-green text-black" data-testid="add-promo-button">
            <Plus className="w-4 h-4 mr-2" />
            New Code
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Discount Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePromo} className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                className="bg-black/50 border-white/10 font-mono uppercase tracking-widest"
                placeholder="e.g., WELCOME20"
                required
                data-testid="promo-code-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={promoForm.discount_type} onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="promo-discount-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{promoForm.discount_type === "percentage" ? "Discount %" : "Discount $"}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={promoForm.discount_value}
                  onChange={(e) => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10"
                  data-testid="promo-discount-value-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Order ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={promoForm.min_order_amount}
                  onChange={(e) => setPromoForm({ ...promoForm, min_order_amount: parseFloat(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10"
                  data-testid="promo-min-order-input"
                />
              </div>
              <div>
                <Label>Max Uses (0 = unlimited)</Label>
                <Input
                  type="number"
                  min="0"
                  value={promoForm.max_uses}
                  onChange={(e) => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10"
                  data-testid="promo-max-uses-input"
                />
              </div>
            </div>
            <div>
              <Label>Expiry Date (optional)</Label>
              <Input
                type="datetime-local"
                value={promoForm.expires_at}
                onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                className="bg-black/50 border-white/10"
                data-testid="promo-expires-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                className="bg-black/50 border-white/10"
                placeholder="Internal note about this code"
                data-testid="promo-description-input"
              />
            </div>
            <Button type="submit" className="w-full bg-neon-green text-black font-bold" data-testid="save-promo-button">
              <Save className="w-4 h-4 mr-2" />
              Create Code
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>

    {promoCodes.length === 0 ? (
      <div className="glass p-8 text-center">
        <Tag className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No discount codes yet</p>
        <p className="text-xs text-zinc-600 mt-1">Create your first promo code to start offering discounts.</p>
      </div>
    ) : (
      <div className="space-y-3">
        {promoCodes.map((promo) => (
          <div
            key={promo.promo_id}
            className={`glass p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${!promo.is_active ? "opacity-50" : ""}`}
            data-testid={`promo-item-${promo.promo_id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-neon-green/10 flex items-center justify-center flex-shrink-0">
                {promo.discount_type === "percentage" ? (
                  <Percent className="w-5 h-5 text-neon-green" />
                ) : (
                  <DollarSign className="w-5 h-5 text-neon-green" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg tracking-wider">{promo.code}</span>
                  <Badge className={promo.is_active ? "bg-neon-green/20 text-neon-green" : "bg-zinc-800 text-zinc-500"}>
                    {promo.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  {promo.discount_type === "percentage"
                    ? `${promo.discount_value}% off`
                    : `$${promo.discount_value.toFixed(2)} off`}
                  {promo.min_order_amount > 0 && ` | Min order: $${promo.min_order_amount.toFixed(2)}`}
                </p>
                {promo.description && <p className="text-xs text-zinc-500 mt-1">{promo.description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-4 sm:flex-shrink-0">
              <div className="text-right text-sm">
                <p className="text-zinc-400">
                  <span className="font-mono">{promo.current_uses}</span>
                  {promo.max_uses > 0 ? ` / ${promo.max_uses}` : ""} uses
                </p>
                {promo.expires_at && (
                  <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    Expires {new Date(promo.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Switch
                checked={promo.is_active}
                onCheckedChange={() => handleTogglePromo(promo.promo_id, promo.is_active)}
                data-testid={`toggle-promo-${promo.promo_id}`}
              />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AIDesignTab = ({
  aiForm, setAiForm, generatingDesign, handleGenerateDesign,
  generatedDesigns, handleDeleteDesign, downloadDesign
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
    <div className="glass p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-neon-cyan to-neon-magenta flex items-center justify-center">
          <Wand2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-heading text-xl uppercase">AI Design Generator</h2>
          <p className="text-xs text-zinc-500">Powered by GPT Image 1</p>
        </div>
      </div>
      <form onSubmit={handleGenerateDesign} className="space-y-4">
        <div>
          <Label>Design Description</Label>
          <Textarea
            value={aiForm.prompt}
            onChange={(e) => setAiForm({ ...aiForm, prompt: e.target.value })}
            placeholder="e.g., A turbo snail mascot with 'Slow Build, Fast Gains' text"
            className="bg-black/50 border-white/10 min-h-[100px]"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Style</Label>
            <Select value={aiForm.style} onValueChange={(v) => setAiForm({ ...aiForm, style: v })}>
              <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="streetwear">Streetwear</SelectItem>
                <SelectItem value="jdm">JDM / Car Culture</SelectItem>
                <SelectItem value="neon">Neon Cyberpunk</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
                <SelectItem value="vintage">Vintage Retro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product Type</Label>
            <Select value={aiForm.product_type} onValueChange={(v) => setAiForm({ ...aiForm, product_type: v })}>
              <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="hoodie">Hoodie</SelectItem>
                <SelectItem value="tshirt">T-Shirt</SelectItem>
                <SelectItem value="poster">Poster</SelectItem>
                <SelectItem value="sticker">Sticker</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" disabled={generatingDesign} className="w-full bg-gradient-to-r from-neon-cyan to-neon-magenta text-black font-bold py-6">
          {generatingDesign ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Design...</>
          ) : (
            <><Sparkles className="w-5 h-5 mr-2" />Generate Design</>
          )}
        </Button>
      </form>
    </div>
    <div>
      <h3 className="font-heading text-lg uppercase mb-4">Generated Designs</h3>
      {generatedDesigns.length === 0 ? (
        <div className="glass p-8 text-center">
          <Sparkles className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No designs generated yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {generatedDesigns.map((design) => (
            <div key={design.design_id} className="glass p-3 group">
              <div className="aspect-square bg-zinc-800 overflow-hidden mb-2">
                <img src={`data:image/png;base64,${design.image_base64}`} alt={design.prompt} className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-zinc-400 truncate mb-2">{design.prompt}</p>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">{design.style}</Badge>
                <Badge variant="outline" className="text-xs">{design.product_type}</Badge>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => downloadDesign(design.image_base64, design.design_id)} className="flex-1 text-xs">
                  <Download className="w-3 h-3 mr-1" />Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDeleteDesign(design.design_id)} className="text-red-500 hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const BeatsTab = ({ beats, showBeatDialog, setShowBeatDialog, beatForm, setBeatForm, handleCreateBeat, handleDeleteBeat, handleEditStems, handleSoundCloudImport, scImportLoading }) => {
  const [uploading, setUploading] = React.useState(false);
  const [bulkFiles, setBulkFiles] = React.useState([]);
  const [bulkBeats, setBulkBeats] = React.useState([]);
  const [autoCovers, setAutoCovers] = React.useState(true);
  const [publishing, setPublishing] = React.useState(false);
  const [coverLoading, setCoverLoading] = React.useState({});
  const [showBulkUpload, setShowBulkUpload] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));
      const res = await axios.post(`${API}/admin/beats/upload-bulk`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      const uploaded = res.data.files.filter(f => !f.error);
      const newBeats = uploaded.map(f => ({
        title: f.original_name.replace(/\.(mp3|wav|flac|ogg|m4a)$/i, "").replace(/[-_]/g, " "),
        audio_url: f.url,
        original_name: f.original_name,
        size_mb: f.size_mb,
        bpm: 140,
        key: "Cm",
        genre: "Trap",
        tags: "",
        vibe: "",
        image_url: "",
        basic_price: 29.99,
        premium_price: 79.99,
        unlimited_price: 149.99,
        exclusive_price: 499.99
      }));
      setBulkBeats(prev => [...prev, ...newBeats]);
      setBulkFiles(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} files uploaded`);
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const generateCover = async (index) => {
    const beat = bulkBeats[index];
    setCoverLoading(prev => ({ ...prev, [index]: true }));
    try {
      const res = await axios.post(`${API}/admin/beats/generate-cover`, {
        title: beat.title, genre: beat.genre, bpm: beat.bpm, key: beat.key,
        tags: beat.tags ? beat.tags.split(",").map(t => t.trim()) : [],
        vibe: beat.vibe
      }, { headers: getAuthHeaders(), withCredentials: true });
      if (res.data.success) {
        const updated = [...bulkBeats];
        updated[index].image_url = res.data.cover_url;
        setBulkBeats(updated);
        toast.success(`Cover generated for "${beat.title}"`);
      }
    } catch (err) {
      toast.error("Cover gen failed: " + (err.response?.data?.detail || err.message));
    }
    setCoverLoading(prev => ({ ...prev, [index]: false }));
  };

  const updateBulkBeat = (index, field, value) => {
    const updated = [...bulkBeats];
    updated[index][field] = value;
    setBulkBeats(updated);
  };

  const removeBulkBeat = (index) => {
    setBulkBeats(prev => prev.filter((_, i) => i !== index));
  };

  const publishAll = async () => {
    if (!bulkBeats.length) return;
    setPublishing(true);
    try {
      const payload = {
        beats: bulkBeats.map(b => ({
          ...b,
          tags: b.tags ? b.tags.split(",").map(t => t.trim()).filter(Boolean) : []
        })),
        auto_cover: autoCovers
      };
      const res = await axios.post(`${API}/admin/beats/create-with-cover`, payload, {
        headers: getAuthHeaders(), withCredentials: true
      });
      toast.success(`${res.data.created} beats published!`);
      setBulkBeats([]);
      setBulkFiles([]);
      window.location.reload();
    } catch (err) {
      toast.error("Publish failed: " + (err.response?.data?.detail || err.message));
    }
    setPublishing(false);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl uppercase">Manage Beats</h2>
        <div className="flex items-center gap-2">
          <Button onClick={handleSoundCloudImport} disabled={scImportLoading} variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20" data-testid="soundcloud-import-btn">
            {scImportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            SoundCloud Import
          </Button>
          <Button onClick={() => setShowBulkUpload(!showBulkUpload)} className="bg-neon-green text-black" data-testid="bulk-upload-toggle">
            <Upload className="w-4 h-4 mr-2" />{showBulkUpload ? "Hide" : "Bulk Upload"}
          </Button>
          <Dialog open={showBeatDialog} onOpenChange={setShowBeatDialog}>
            <DialogTrigger asChild>
              <Button className="bg-neon-cyan text-black" data-testid="add-beat-button">
                <Plus className="w-4 h-4 mr-2" />Add Beat
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add New Beat</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateBeat} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={beatForm.title} onChange={(e) => setBeatForm({ ...beatForm, title: e.target.value })} className="bg-black/50 border-white/10" required /></div>
                <div><Label>Genre</Label>
                  <Select value={beatForm.genre} onValueChange={(v) => setBeatForm({ ...beatForm, genre: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {["Trap","Hip-Hop","Drill","Lo-Fi","R&B","Pop","Phonk","Afrobeats"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>BPM</Label><Input type="number" value={beatForm.bpm} onChange={(e) => setBeatForm({ ...beatForm, bpm: parseInt(e.target.value) })} className="bg-black/50 border-white/10" /></div>
                <div><Label>Key</Label><Input value={beatForm.key} onChange={(e) => setBeatForm({ ...beatForm, key: e.target.value })} className="bg-black/50 border-white/10" /></div>
              </div>
              <div><Label>Audio URL</Label><Input value={beatForm.audio_url} onChange={(e) => setBeatForm({ ...beatForm, audio_url: e.target.value })} className="bg-black/50 border-white/10" required placeholder="https://..." /></div>
              <div><Label>Image URL</Label><Input value={beatForm.image_url} onChange={(e) => setBeatForm({ ...beatForm, image_url: e.target.value })} className="bg-black/50 border-white/10" placeholder="https://..." /></div>
              <div><Label>Tags (comma separated)</Label><Input value={beatForm.tags} onChange={(e) => setBeatForm({ ...beatForm, tags: e.target.value })} className="bg-black/50 border-white/10" placeholder="dark, aggressive, 808" /></div>
              <div className="grid grid-cols-4 gap-4">
                <div><Label>Basic $</Label><Input type="number" step="0.01" value={beatForm.basic_price} onChange={(e) => setBeatForm({ ...beatForm, basic_price: parseFloat(e.target.value) })} className="bg-black/50 border-white/10" /></div>
                <div><Label>Premium $</Label><Input type="number" step="0.01" value={beatForm.premium_price} onChange={(e) => setBeatForm({ ...beatForm, premium_price: parseFloat(e.target.value) })} className="bg-black/50 border-white/10" /></div>
                <div><Label>Unlimited $</Label><Input type="number" step="0.01" value={beatForm.unlimited_price} onChange={(e) => setBeatForm({ ...beatForm, unlimited_price: parseFloat(e.target.value) })} className="bg-black/50 border-white/10" /></div>
                <div><Label>Exclusive $</Label><Input type="number" step="0.01" value={beatForm.exclusive_price} onChange={(e) => setBeatForm({ ...beatForm, exclusive_price: parseFloat(e.target.value) })} className="bg-black/50 border-white/10" /></div>
              </div>
              <Button type="submit" className="w-full bg-neon-cyan text-black" data-testid="save-beat-button"><Save className="w-4 h-4 mr-2" />Save Beat</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      {showBulkUpload && (
        <div className="glass border border-neon-green/20 p-6 mb-6 space-y-4" data-testid="bulk-upload-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg uppercase text-neon-green">Bulk Beat Upload</h3>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={autoCovers} onChange={(e) => setAutoCovers(e.target.checked)} className="rounded" />
                Auto-generate AI covers
              </label>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-white/10 hover:border-neon-green/40 rounded-lg p-8 text-center cursor-pointer transition-colors"
            data-testid="beat-drop-zone"
          >
            <input ref={fileInputRef} type="file" multiple accept=".mp3,.wav,.flac,.ogg,.m4a" onChange={handleFileSelect} className="hidden" />
            {uploading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-6 h-6 text-neon-green animate-spin" />
                <span className="text-zinc-400">Uploading files...</span>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">Click to select audio files (MP3, WAV, FLAC, OGG, M4A)</p>
                <p className="text-zinc-600 text-xs mt-1">Upload multiple files at once</p>
              </>
            )}
          </div>

          {/* Queued Beats */}
          {bulkBeats.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">{bulkBeats.length} beats queued</p>
                <Button onClick={publishAll} disabled={publishing} className="bg-neon-green text-black font-bold" data-testid="publish-all-btn">
                  {publishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publishing...</> : <><Sparkles className="w-4 h-4 mr-2" />Publish All ({bulkBeats.length})</>}
                </Button>
              </div>

              {bulkBeats.map((beat, i) => (
                <div key={i} className="glass border border-white/5 p-4 space-y-3" data-testid={`bulk-beat-${i}`}>
                  <div className="flex items-start gap-4">
                    {/* Cover Preview */}
                    <div className="w-20 h-20 bg-zinc-800 rounded flex-shrink-0 overflow-hidden relative group">
                      {beat.image_url ? (
                        <img src={beat.image_url.startsWith("/") ? `${BACKEND_URL}${beat.image_url}` : beat.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-600">
                          <ImageIcon className="w-6 h-6" />
                        </div>
                      )}
                      <button
                        onClick={() => generateCover(i)}
                        disabled={coverLoading[i]}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        {coverLoading[i] ? <Loader2 className="w-5 h-5 text-neon-yellow animate-spin" /> : <Sparkles className="w-5 h-5 text-neon-yellow" />}
                      </button>
                    </div>

                    {/* Main Fields */}
                    <div className="flex-1 grid grid-cols-4 gap-3">
                      <div className="col-span-2">
                        <Label className="text-[11px] text-zinc-500">Title</Label>
                        <Input value={beat.title} onChange={(e) => updateBulkBeat(i, "title", e.target.value)} className="bg-black/50 border-white/10 h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-zinc-500">Genre</Label>
                        <Select value={beat.genre} onValueChange={(v) => updateBulkBeat(i, "genre", v)}>
                          <SelectTrigger className="bg-black/50 border-white/10 h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            {["Trap","Hip-Hop","Drill","Lo-Fi","R&B","Pop","Phonk","Afrobeats"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-[11px] text-zinc-500">BPM</Label>
                          <Input type="number" value={beat.bpm} onChange={(e) => updateBulkBeat(i, "bpm", parseInt(e.target.value) || 0)} className="bg-black/50 border-white/10 h-8 text-sm" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[11px] text-zinc-500">Key</Label>
                          <Input value={beat.key} onChange={(e) => updateBulkBeat(i, "key", e.target.value)} className="bg-black/50 border-white/10 h-8 text-sm" />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[11px] text-zinc-500">Tags (comma separated)</Label>
                        <Input value={beat.tags} onChange={(e) => updateBulkBeat(i, "tags", e.target.value)} className="bg-black/50 border-white/10 h-8 text-sm" placeholder="dark, 808, hard" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[11px] text-zinc-500">Vibe (for AI cover)</Label>
                        <Input value={beat.vibe} onChange={(e) => updateBulkBeat(i, "vibe", e.target.value)} className="bg-black/50 border-white/10 h-8 text-sm" placeholder="dark moody, vibrant neon, chill..." />
                      </div>
                    </div>

                    {/* Remove */}
                    <Button variant="ghost" size="icon" onClick={() => removeBulkBeat(i)} className="text-zinc-500 hover:text-red-500 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Pricing Row */}
                  <div className="grid grid-cols-4 gap-3 pl-24">
                    <div><Label className="text-[11px] text-zinc-500">Basic $</Label><Input type="number" step="0.01" value={beat.basic_price} onChange={(e) => updateBulkBeat(i, "basic_price", parseFloat(e.target.value) || 0)} className="bg-black/50 border-white/10 h-8 text-sm" /></div>
                    <div><Label className="text-[11px] text-zinc-500">Premium $</Label><Input type="number" step="0.01" value={beat.premium_price} onChange={(e) => updateBulkBeat(i, "premium_price", parseFloat(e.target.value) || 0)} className="bg-black/50 border-white/10 h-8 text-sm" /></div>
                    <div><Label className="text-[11px] text-zinc-500">Unlimited $</Label><Input type="number" step="0.01" value={beat.unlimited_price} onChange={(e) => updateBulkBeat(i, "unlimited_price", parseFloat(e.target.value) || 0)} className="bg-black/50 border-white/10 h-8 text-sm" /></div>
                    <div><Label className="text-[11px] text-zinc-500">Exclusive $</Label><Input type="number" step="0.01" value={beat.exclusive_price} onChange={(e) => updateBulkBeat(i, "exclusive_price", parseFloat(e.target.value) || 0)} className="bg-black/50 border-white/10 h-8 text-sm" /></div>
                  </div>

                  <div className="text-[11px] text-zinc-600 pl-24">{beat.original_name} ({beat.size_mb} MB)</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Existing Beats List */}
      <div className="space-y-3">
        {beats.map((beat) => (
          <div key={beat.beat_id} className="glass p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-zinc-800 overflow-hidden">
                <img src={beat.image_url ? (beat.image_url.startsWith("/") ? `${BACKEND_URL}${beat.image_url}` : beat.image_url) : ""} alt={beat.title} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-medium">{beat.title}</p>
                <div className="flex gap-2 text-xs text-zinc-500">
                  <span>{beat.bpm} BPM</span><span>|</span><span>{beat.key}</span><span>|</span><span>{beat.genre}</span>
                  {beat.stems?.length > 0 && (<><span>|</span><span className="text-neon-green">{beat.stems.length} stems</span></>)}
                  {beat.soundcloud_id && (<><span>|</span><span className="text-orange-400">SC</span></>)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-neon-cyan text-sm">${beat.basic_price}</span>
              <Button variant="ghost" size="icon" onClick={() => handleEditStems(beat.beat_id)} className="text-zinc-400 hover:text-neon-green" data-testid={`stems-btn-${beat.beat_id}`} title="Manage Stems"><Layers className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => handleDeleteBeat(beat.beat_id)} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const MerchTab = ({ merch, showMerchDialog, setShowMerchDialog, merchForm, setMerchForm, handleCreateMerch, handleDeleteMerch }) => (
  <>
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Manage Merch</h2>
      <Dialog open={showMerchDialog} onOpenChange={setShowMerchDialog}>
        <DialogTrigger asChild>
          <Button className="bg-neon-magenta text-black" data-testid="add-merch-button">
            <Plus className="w-4 h-4 mr-2" />Add Merch
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Add New Merch</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateMerch} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={merchForm.name} onChange={(e) => setMerchForm({ ...merchForm, name: e.target.value })} className="bg-black/50 border-white/10" required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={merchForm.description} onChange={(e) => setMerchForm({ ...merchForm, description: e.target.value })} className="bg-black/50 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={merchForm.category} onValueChange={(v) => setMerchForm({ ...merchForm, category: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="hoodies">Hoodies</SelectItem>
                    <SelectItem value="shirts">T-Shirts</SelectItem>
                    <SelectItem value="pants">Pants</SelectItem>
                    <SelectItem value="jackets">Jackets</SelectItem>
                    <SelectItem value="phone_cases">Phone Cases</SelectItem>
                    <SelectItem value="stickers">Stickers</SelectItem>
                    <SelectItem value="cups">Cups</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price</Label>
                <Input type="number" step="0.01" value={merchForm.price} onChange={(e) => setMerchForm({ ...merchForm, price: parseFloat(e.target.value) })} className="bg-black/50 border-white/10" />
              </div>
            </div>
            <div>
              <Label>Image URLs (comma separated)</Label>
              <Input value={merchForm.images} onChange={(e) => setMerchForm({ ...merchForm, images: e.target.value })} className="bg-black/50 border-white/10" placeholder="https://..., https://..." />
            </div>
            <div>
              <Label>Variants (JSON array)</Label>
              <Textarea value={merchForm.variants} onChange={(e) => setMerchForm({ ...merchForm, variants: e.target.value })} className="bg-black/50 border-white/10 font-mono text-xs" placeholder='[{"size": "M", "color": "Black", "stock": 10}]' />
            </div>
            <Button type="submit" className="w-full bg-neon-magenta text-black" data-testid="save-merch-button">
              <Save className="w-4 h-4 mr-2" />Save Merch
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {merch.map((item) => (
        <div key={item.merch_id} className="glass p-4">
          <div className="aspect-square bg-zinc-800 overflow-hidden mb-3">
            <img src={item.images?.[0] || ""} alt={item.name} className="w-full h-full object-cover" />
          </div>
          <p className="font-medium truncate">{item.name}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="font-mono text-neon-magenta">${item.price}</span>
            <Button variant="ghost" size="icon" onClick={() => handleDeleteMerch(item.merch_id)} className="text-zinc-400 hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  </>
);

const CarsTab = ({ showCarDialog, setShowCarDialog, carForm, setCarForm, handleCreateCar }) => {
  const [carMedia, setCarMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchCarMedia();
  }, []);

  const fetchCarMedia = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/cars`, { headers, withCredentials: true });
      setCarMedia(res.data);
    } catch (e) { console.error("Failed to fetch car media"); }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle);
      formData.append("description", uploadDesc);
      formData.append("tags", uploadTags);
      await axios.post(`${API}/admin/cars/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Media uploaded successfully!");
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadTitle("");
      setUploadDesc("");
      setUploadTags("");
      fetchCarMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCar = async (mediaId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/cars/${mediaId}`, { headers, withCredentials: true });
      toast.success("Media deleted");
      fetchCarMedia();
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl uppercase">Manage Car Content</h2>
        <div className="flex gap-2">
          {/* File Upload Dialog */}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button className="bg-neon-cyan text-black" data-testid="upload-car-button">
                <Upload className="w-4 h-4 mr-2" />Upload File
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Upload Car Media</DialogTitle></DialogHeader>
              <form onSubmit={handleFileUpload} className="space-y-4">
                <div>
                  <Label>File (Image or Video)</Label>
                  <Input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="bg-black/50 border-white/10"
                    required
                    data-testid="car-file-input"
                  />
                  {uploadFile && (
                    <p className="text-xs text-zinc-500 mt-1">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>
                  )}
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} className="bg-black/50 border-white/10" required placeholder="JDM Night Meet" data-testid="car-upload-title" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} className="bg-black/50 border-white/10" placeholder="Describe the content..." />
                </div>
                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} className="bg-black/50 border-white/10" placeholder="jdm, night, meet" />
                </div>
                <Button type="submit" className="w-full bg-neon-cyan text-black font-bold" disabled={uploading} data-testid="submit-car-upload">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload Media</>}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          {/* URL-based Add Dialog */}
          <Dialog open={showCarDialog} onOpenChange={setShowCarDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/20" data-testid="add-car-button">
                <Plus className="w-4 h-4 mr-2" />Add via URL
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Add Car Media (URL)</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateCar} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={carForm.title} onChange={(e) => setCarForm({ ...carForm, title: e.target.value })} className="bg-black/50 border-white/10" required />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={carForm.description} onChange={(e) => setCarForm({ ...carForm, description: e.target.value })} className="bg-black/50 border-white/10" />
                </div>
                <div>
                  <Label>Media Type</Label>
                  <Select value={carForm.media_type} onValueChange={(v) => setCarForm({ ...carForm, media_type: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Media URL</Label>
                  <Input value={carForm.media_url} onChange={(e) => setCarForm({ ...carForm, media_url: e.target.value })} className="bg-black/50 border-white/10" required placeholder="https://..." />
                </div>
                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input value={carForm.tags} onChange={(e) => setCarForm({ ...carForm, tags: e.target.value })} className="bg-black/50 border-white/10" placeholder="jdm, night, meet" />
                </div>
                <Button type="submit" className="w-full bg-neon-green text-black" data-testid="save-car-button">
                  <Save className="w-4 h-4 mr-2" />Save Media
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Car Media Grid */}
      {carMedia.length === 0 ? (
        <div className="glass p-8 text-center">
          <Car className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No car media yet. Upload some photos or videos!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {carMedia.map((item) => (
            <div key={item.media_id} className="glass overflow-hidden group" data-testid={`admin-car-${item.media_id}`}>
              <div className="aspect-video relative overflow-hidden">
                <img
                  src={item.thumbnail_url || item.media_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' fill='%2318181b'%3E%3Crect width='400' height='225'/%3E%3Ctext x='50%25' y='50%25' fill='%2352525b' text-anchor='middle' dominant-baseline='middle' font-size='14'%3ENo Preview%3C/text%3E%3C/svg%3E"; }}
                />
                <Badge className="absolute top-2 left-2 bg-black/60 text-white border-0 text-xs">
                  {item.media_type === "video" ? "Video" : "Photo"}
                </Badge>
              </div>
              <div className="p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{item.title}</p>
                  <p className="text-xs text-zinc-500">{item.views} views</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDeleteCar(item.media_id)} className="text-zinc-400 hover:text-red-500 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const UsersTab = ({ users, currentUserId, handleToggleAdmin }) => (
  <>
    <h2 className="font-heading text-xl uppercase mb-6">Manage Users</h2>
    <div className="space-y-3">
      {users.map((u) => (
        <div key={u.user_id} className="glass p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-zinc-800 rounded-full overflow-hidden">
              {u.picture ? (
                <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-500">
                  {u.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
            </div>
            <div>
              <p className="font-medium">{u.name}</p>
              <p className="text-xs text-zinc-500">{u.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {u.is_admin && (
              <Badge className="bg-neon-green/20 text-neon-green">Admin</Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleAdmin(u.user_id)}
              className="border-white/20"
              disabled={u.user_id === currentUserId}
            >
              {u.is_admin ? "Remove Admin" : "Make Admin"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  </>
);

const LowStockTab = ({ items }) => (
  <div data-testid="low-stock-alerts">
    <div className="flex items-center gap-3 mb-6">
      <AlertTriangle className="w-6 h-6 text-red-400" />
      <h2 className="font-heading text-xl uppercase">Low Stock Alerts</h2>
      <Badge className="bg-red-500/20 text-red-400">{items.length} items</Badge>
    </div>
    
    {items.length === 0 ? (
      <div className="glass p-8 text-center">
        <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">All stock levels look good!</p>
      </div>
    ) : (
      <div className="space-y-3">
        {items.map((item, i) => (
          <div
            key={`${item.merch_id}-${item.size}-${item.color}-${i}`}
            className={`glass p-5 flex items-center gap-4 ${item.stock === 0 ? "border-l-4 border-red-500" : "border-l-4 border-neon-yellow"}`}
            data-testid={`low-stock-item-${i}`}
          >
            {item.image && (
              <div className="w-14 h-14 bg-zinc-800 overflow-hidden flex-shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium">{item.name}</p>
              <div className="flex gap-2 mt-1">
                {item.size && <Badge variant="outline" className="text-xs border-white/20">Size: {item.size}</Badge>}
                {item.color && <Badge variant="outline" className="text-xs border-white/20">Color: {item.color}</Badge>}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className={`font-mono text-2xl font-bold ${item.stock === 0 ? "text-red-500" : "text-neon-yellow"}`}>
                {item.stock}
              </p>
              <p className="text-xs text-zinc-500">
                {item.stock === 0 ? "OUT OF STOCK" : "remaining"}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const EmailCampaignsTab = ({
  campaigns, subscribers, showCampaignDialog, setShowCampaignDialog,
  campaignForm, setCampaignForm, handleCreateCampaign
}) => (
  <div data-testid="email-campaigns">
    <div className="flex justify-between items-center mb-6">
      <div>
        <h2 className="font-heading text-xl uppercase">Email Campaigns</h2>
        <p className="text-sm text-zinc-500 mt-1">
          {subscribers.total} subscriber{subscribers.total !== 1 ? "s" : ""}
          <Badge className="ml-2 bg-neon-green/20 text-neon-green border-neon-green/50 text-xs">SendGrid Connected</Badge>
        </p>
      </div>
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogTrigger asChild>
          <Button className="bg-blue-500 text-white" data-testid="new-campaign-button">
            <Send className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Email Campaign</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCampaign} className="space-y-4">
            <div>
              <Label>Subject Line</Label>
              <Input
                value={campaignForm.subject}
                onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                className="bg-black/50 border-white/10"
                placeholder="New Beats Dropped!"
                required
                data-testid="campaign-subject-input"
              />
            </div>
            <div>
              <Label>Email Body (HTML supported)</Label>
              <Textarea
                value={campaignForm.body}
                onChange={(e) => setCampaignForm({ ...campaignForm, body: e.target.value })}
                className="bg-black/50 border-white/10 min-h-[150px]"
                placeholder="Write your email content here..."
                required
                data-testid="campaign-body-input"
              />
            </div>
            <div>
              <Label>Send To</Label>
              <Select value={campaignForm.recipient_type} onValueChange={(v) => setCampaignForm({ ...campaignForm, recipient_type: v })}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="campaign-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all">All Users & Subscribers</SelectItem>
                  <SelectItem value="subscribers">Newsletter Subscribers Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-blue-500 text-white font-bold" data-testid="send-campaign-button">
              <Send className="w-4 h-4 mr-2" />
              Send Campaign
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>

    {campaigns.length === 0 ? (
      <div className="glass p-8 text-center">
        <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No campaigns sent yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {campaigns.map((camp) => (
          <div key={camp.campaign_id} className="glass p-5" data-testid={`campaign-${camp.campaign_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-lg">{camp.subject}</h3>
                <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{camp.body}</p>
              </div>
              <Badge className={
                camp.status === "sent" ? "bg-neon-green/20 text-neon-green" :
                camp.status === "failed" ? "bg-red-500/20 text-red-400" :
                "bg-yellow-500/20 text-yellow-400"
              }>
                {camp.status === "sent" ? "Sent" : camp.status === "failed" ? "Failed" : "Mocked"}
              </Badge>
            </div>
            <div className="flex gap-4 mt-3 text-xs text-zinc-500">
              <span>{camp.recipient_count} recipients</span>
              {camp.sent_count > 0 && <span className="text-neon-green">{camp.sent_count} delivered</span>}
              {camp.failed_count > 0 && <span className="text-red-400">{camp.failed_count} failed</span>}
              <span>{new Date(camp.created_at).toLocaleDateString()}</span>
              <span>{camp.recipient_type === "all" ? "All users" : "Subscribers only"}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const VoiceTagTab = ({ voiceTagExists, generatingTag, handleGenerateVoiceTag }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  return (
    <div data-testid="voice-tag-manager">
      <h2 className="font-heading text-xl uppercase mb-6">Beat Preview Protection</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mic2 className="w-6 h-6 text-orange-400" />
            <h3 className="font-heading text-lg uppercase">Voice Tag</h3>
          </div>
          <p className="text-sm text-zinc-400 mb-6">
            Generate a voice tag that says "Welcome to the gas station, Time to fill your tank up" 
            using AI text-to-speech. This tag is overlaid on beat previews to protect your music.
          </p>
          
          {voiceTagExists ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-neon-green">
                <Volume2 className="w-5 h-5" />
                <span className="font-medium">Voice tag ready</span>
              </div>
              <audio controls className="w-full" data-testid="voice-tag-audio">
                <source src={`${API}/static/voice_tag.mp3`} type="audio/mpeg" />
              </audio>
              <Button onClick={handleGenerateVoiceTag} disabled={generatingTag} variant="outline" className="border-white/20 w-full" data-testid="regenerate-voice-tag">
                {generatingTag ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Regenerating...</> : "Regenerate Voice Tag"}
              </Button>
            </div>
          ) : (
            <Button onClick={handleGenerateVoiceTag} disabled={generatingTag} className="bg-orange-500 text-white w-full py-6" data-testid="generate-voice-tag-button">
              {generatingTag ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Voice Tag...</>
              ) : (
                <><Mic2 className="w-5 h-5 mr-2" />Generate Voice Tag</>
              )}
            </Button>
          )}
        </div>
        
        <div className="glass p-6">
          <h3 className="font-heading text-lg uppercase mb-4">How It Works</h3>
          <div className="space-y-4 text-sm text-zinc-400">
            <div className="flex gap-3">
              <span className="w-6 h-6 bg-orange-500/20 text-orange-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <p>Generate your custom voice tag using AI text-to-speech (deep male radio voice).</p>
            </div>
            <div className="flex gap-3">
              <span className="w-6 h-6 bg-orange-500/20 text-orange-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <p>The voice tag is automatically overlaid on beat preview audio at the beginning and middle of each track.</p>
            </div>
            <div className="flex gap-3">
              <span className="w-6 h-6 bg-orange-500/20 text-orange-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
              <p>Customers hear the watermarked preview. After purchasing a license, they receive the clean, unwatermarked audio files.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogAdminTab = ({
  blogPosts, showBlogDialog, setShowBlogDialog, blogForm, setBlogForm,
  handleCreateBlogPost, handleDeleteBlogPost,
  showBulkDialog, setShowBulkDialog, bulkBeats, setBulkBeats, handleBulkUpload
}) => (
  <div data-testid="blog-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Blog & Bulk Upload</h2>
      <div className="flex gap-2">
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-neon-cyan text-neon-cyan" data-testid="bulk-upload-button">
              <Upload className="w-4 h-4 mr-2" />Bulk Upload Beats
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Bulk Upload Beats</DialogTitle></DialogHeader>
            <form onSubmit={handleBulkUpload} className="space-y-4">
              {bulkBeats.map((beat, i) => (
                <div key={i} className="p-4 bg-white/5 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-mono text-zinc-400">Beat #{i + 1}</span>
                    {bulkBeats.length > 1 && (
                      <button type="button" onClick={() => setBulkBeats(prev => prev.filter((_, j) => j !== i))} className="text-red-500 text-xs">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={beat.title} onChange={(e) => { const n = [...bulkBeats]; n[i].title = e.target.value; setBulkBeats(n); }} placeholder="Title" className="bg-black/50 border-white/10" required />
                    <Input value={beat.audio_url} onChange={(e) => { const n = [...bulkBeats]; n[i].audio_url = e.target.value; setBulkBeats(n); }} placeholder="Audio URL" className="bg-black/50 border-white/10" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="number" value={beat.bpm} onChange={(e) => { const n = [...bulkBeats]; n[i].bpm = parseInt(e.target.value); setBulkBeats(n); }} placeholder="BPM" className="bg-black/50 border-white/10" />
                    <Input value={beat.key} onChange={(e) => { const n = [...bulkBeats]; n[i].key = e.target.value; setBulkBeats(n); }} placeholder="Key" className="bg-black/50 border-white/10" />
                    <Input value={beat.genre} onChange={(e) => { const n = [...bulkBeats]; n[i].genre = e.target.value; setBulkBeats(n); }} placeholder="Genre" className="bg-black/50 border-white/10" />
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" className="w-full border-dashed border-white/20" onClick={() => setBulkBeats(prev => [...prev, { title: "", bpm: 140, key: "Am", genre: "Trap", audio_url: "", image_url: "", basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99 }])}>
                <Plus className="w-4 h-4 mr-2" />Add Another Beat
              </Button>
              <Button type="submit" className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-bulk-upload">
                <Upload className="w-4 h-4 mr-2" />Upload {bulkBeats.length} Beat{bulkBeats.length > 1 ? "s" : ""}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={showBlogDialog} onOpenChange={setShowBlogDialog}>
          <DialogTrigger asChild>
            <Button className="bg-white text-black" data-testid="new-blog-post-button">
              <Plus className="w-4 h-4 mr-2" />New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create Blog Post</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateBlogPost} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} className="bg-black/50 border-white/10" required data-testid="blog-title-input" />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} className="bg-black/50 border-white/10 min-h-[200px]" required data-testid="blog-content-input" />
              </div>
              <div>
                <Label>Excerpt (optional)</Label>
                <Input value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} className="bg-black/50 border-white/10" />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input value={blogForm.image_url} onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })} className="bg-black/50 border-white/10" placeholder="https://..." />
              </div>
              <div>
                <Label>Tags (comma separated)</Label>
                <Input value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} className="bg-black/50 border-white/10" placeholder="car meet, jdm, new drop" />
              </div>
              <Button type="submit" className="w-full bg-white text-black font-bold" data-testid="save-blog-post">
                <Save className="w-4 h-4 mr-2" />Publish Post
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    {blogPosts.length === 0 ? (
      <div className="glass p-8 text-center">
        <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No blog posts yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {blogPosts.map((post) => (
          <div key={post.post_id} className="glass p-5 flex items-center gap-4" data-testid={`blog-admin-${post.post_id}`}>
            {post.image_url && <img src={post.image_url} alt="" className="w-16 h-16 object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium truncate">{post.title}</p>
                <Badge className={post.published ? "bg-neon-green/20 text-neon-green" : "bg-zinc-600 text-zinc-400"}>
                  {post.published ? "Published" : "Draft"}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{new Date(post.created_at).toLocaleDateString()} | /{post.slug}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => handleDeleteBlogPost(post.post_id)} className="text-zinc-400 hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const LiveChatAdminTab = ({
  chatSessions, activeChatId, openChat, chatMessages, chatReply, setChatReply, handleSendReply
}) => (
  <div data-testid="live-chat-admin" className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {/* Sessions List */}
    <div className="md:col-span-1">
      <h2 className="font-heading text-xl uppercase mb-4">Chat Sessions</h2>
      {chatSessions.length === 0 ? (
        <div className="glass p-6 text-center">
          <MessageCircle className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No chats yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {chatSessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => openChat(s.session_id)}
              className={`w-full text-left glass p-4 transition-colors ${activeChatId === s.session_id ? "border-l-4 border-neon-cyan" : "hover:bg-white/5"}`}
              data-testid={`chat-session-${s.session_id}`}
            >
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">{s.user_name}</p>
                <Badge className={s.status === "open" ? "bg-neon-green/20 text-neon-green text-xs" : "bg-zinc-700 text-zinc-400 text-xs"}>
                  {s.status}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500 truncate mt-1">{s.last_message || "No messages"}</p>
              <p className="text-xs text-zinc-600 mt-1">{s.message_count} msgs</p>
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Chat Messages */}
    <div className="md:col-span-2">
      {activeChatId ? (
        <div className="glass flex flex-col h-[500px]">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-heading text-sm uppercase">Chat #{activeChatId.slice(-8)}</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatMessages.map((msg) => (
              <div key={msg.message_id} className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[70%] p-3 text-sm ${msg.sender_type === "admin" ? "bg-neon-cyan/20" : "bg-white/10"}`}>
                  <p className="text-xs font-medium mb-1 text-zinc-500">{msg.sender_name}</p>
                  <p>{msg.message}</p>
                  <p className="text-xs text-zinc-600 mt-1">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSendReply} className="p-3 border-t border-white/10 flex gap-2">
            <Input value={chatReply} onChange={(e) => setChatReply(e.target.value)} placeholder="Reply..." className="bg-black/50 border-white/10 flex-1" data-testid="admin-chat-input" />
            <Button type="submit" size="icon" className="bg-neon-cyan text-black" data-testid="admin-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      ) : (
        <div className="glass p-12 text-center h-[500px] flex items-center justify-center">
          <div>
            <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">Select a chat to view messages</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

const AffiliatesTab = ({ affiliates, payouts = [], handleApprovePayout }) => (
  <div data-testid="affiliates-admin">
    <h2 className="font-heading text-xl uppercase mb-6">Affiliate Program</h2>

    {/* Payout Requests */}
    {payouts.length > 0 && (
      <div className="mb-8">
        <h3 className="text-sm uppercase text-amber-400 tracking-wide mb-3">Payout Requests ({payouts.filter(p => p.status === 'pending').length} pending)</h3>
        <div className="space-y-2">
          {payouts.map((p) => (
            <div key={p.payout_id} className="glass p-4 flex items-center gap-4" data-testid={`payout-${p.payout_id}`}>
              <div className="flex-1">
                <p className="text-sm">Payout: <span className="text-neon-green font-mono">${p.amount?.toFixed(2)}</span></p>
                <p className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</p>
              </div>
              <Badge className={p.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-neon-green/20 text-neon-green'}>{p.status}</Badge>
              {p.status === 'pending' && handleApprovePayout && (
                <Button size="sm" onClick={() => handleApprovePayout(p.payout_id)} className="bg-neon-green text-black font-bold" data-testid={`approve-payout-${p.payout_id}`}>Approve</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {affiliates.length === 0 ? (
      <div className="glass p-8 text-center">
        <Link2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No affiliates enrolled yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {affiliates.map((aff) => (
          <div key={aff.affiliate_id} className="glass p-5 flex items-center gap-4" data-testid={`affiliate-${aff.affiliate_id}`}>
            <div className="flex-1">
              <p className="font-medium">{aff.user_name}</p>
              <p className="text-xs text-zinc-500 font-mono mt-1">Code: {aff.referral_code} | Commission: {aff.commission_rate || 10}%</p>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="font-heading text-lg text-neon-cyan">{aff.total_referrals}</p>
                <p className="text-xs text-zinc-500">Referrals</p>
              </div>
              <div>
                <p className="font-heading text-lg text-neon-green">${(aff.total_earnings || 0).toFixed(2)}</p>
                <p className="text-xs text-zinc-500">Earned</p>
              </div>
              <div>
                <p className="font-heading text-lg text-neon-yellow">${(aff.pending_earnings || 0).toFixed(2)}</p>
                <p className="text-xs text-zinc-500">Pending</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AbandonedCartsTab = ({ carts, handleMarkContacted, handleAutoRecover, autoRecoverLoading, recoveryLogs }) => (
  <div data-testid="abandoned-carts-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Abandoned Cart Recovery</h2>
      <div className="flex items-center gap-2">
        <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50">
          SendGrid - Recovery emails active
        </Badge>
        <Button
          size="sm"
          onClick={handleAutoRecover}
          disabled={autoRecoverLoading}
          className="bg-amber-500 text-black font-bold uppercase text-xs"
          data-testid="auto-recover-btn"
        >
          {autoRecoverLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
          Auto-Recover All
        </Button>
      </div>
    </div>
    <p className="text-sm text-zinc-400 mb-6">
      Carts with items that haven't been checked out in the last 24 hours. Use "Auto-Recover All" to send recovery emails to all pending carts at once.
    </p>

    {/* Recovery Log History */}
    {recoveryLogs && recoveryLogs.length > 0 && (
      <div className="mb-6 glass p-4" data-testid="recovery-logs">
        <p className="text-xs text-zinc-500 uppercase tracking-wide mb-3">Recent Auto-Recovery Runs</p>
        <div className="space-y-2">
          {recoveryLogs.slice(0, 5).map((log, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs text-zinc-400 px-2 py-1 bg-white/5">
              <span>{new Date(log.run_at).toLocaleString()}</span>
              <span>{log.carts_found} found / {log.emails_sent} sent</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {carts.total === 0 ? (
      <div className="glass p-8 text-center">
        <ShoppingCart className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No abandoned carts found</p>
      </div>
    ) : (
      <div className="space-y-3">
        {carts.carts.map((cart, idx) => (
          <div key={idx} className="glass p-5 flex items-center gap-4" data-testid={`abandoned-cart-${cart.user_id}`}>
            <div className="flex-1">
              <p className="font-medium">{cart.user_name}</p>
              <p className="text-xs text-zinc-500">{cart.user_email}</p>
              <p className="text-xs text-zinc-600 mt-1">
                {cart.item_count} item{cart.item_count !== 1 ? 's' : ''}: {cart.item_names.join(', ')}
              </p>
            </div>
            <div className="text-center mr-4">
              <p className="font-heading text-lg text-neon-yellow">${cart.cart_value.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">Cart Value</p>
            </div>
            <div className="text-center mr-4">
              <Badge className={cart.recovery_status === "contacted" 
                ? "bg-neon-green/20 text-neon-green border-neon-green/50" 
                : "bg-amber-500/20 text-amber-400 border-amber-500/50"
              }>
                {cart.recovery_status === "contacted" ? "Email Sent" : "Pending"}
              </Badge>
            </div>
            {cart.recovery_status !== "contacted" && (
              <Button
                size="sm"
                onClick={() => handleMarkContacted(cart.user_id)}
                className="bg-amber-500 text-black font-bold uppercase text-xs"
                data-testid={`contact-btn-${cart.user_id}`}
              >
                <Send className="w-3 h-3 mr-1" /> Send Recovery Email
              </Button>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ABTestingTab = ({ abTests, merch, showAbTestDialog, setShowAbTestDialog, abTestForm, setAbTestForm, handleCreateAbTest, handleToggleAbTest, handleDeleteAbTest }) => (
  <div data-testid="ab-testing-admin">
    <div className="flex justify-between items-center mb-6">
      <div>
        <h2 className="font-heading text-xl uppercase">A/B Testing</h2>
        <p className="text-xs text-zinc-500 mt-1">Live tracking with auto-winner declaration at 95% confidence (min 100 views each).</p>
      </div>
      <div className="flex items-center gap-3">
        <Dialog open={showAbTestDialog} onOpenChange={setShowAbTestDialog}>
          <DialogTrigger asChild>
            <Button className="bg-purple-500 text-black" data-testid="create-ab-test-button">
              <Plus className="w-4 h-4 mr-2" />New Test
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Create A/B Test</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateAbTest} className="space-y-4">
              <div>
                <Label>Test Name</Label>
                <Input value={abTestForm.name} onChange={(e) => setAbTestForm({ ...abTestForm, name: e.target.value })} className="bg-black/50 border-white/10" required placeholder="e.g., Hoodie pricing test" />
              </div>
              <div>
                <Label>Merch Item</Label>
                <Select value={abTestForm.merch_id} onValueChange={(v) => setAbTestForm({ ...abTestForm, merch_id: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10"><SelectValue placeholder="Select item..." /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {merch.map((m) => (
                      <SelectItem key={m.merch_id} value={m.merch_id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Variant A Label</Label>
                  <Input value={abTestForm.variant_a_label} onChange={(e) => setAbTestForm({ ...abTestForm, variant_a_label: e.target.value })} className="bg-black/50 border-white/10" />
                </div>
                <div>
                  <Label>Variant B Label</Label>
                  <Input value={abTestForm.variant_b_label} onChange={(e) => setAbTestForm({ ...abTestForm, variant_b_label: e.target.value })} className="bg-black/50 border-white/10" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Variant A Value</Label>
                  <Input value={abTestForm.variant_a_value} onChange={(e) => setAbTestForm({ ...abTestForm, variant_a_value: e.target.value })} className="bg-black/50 border-white/10" placeholder="e.g., $79.99" />
                </div>
                <div>
                  <Label>Variant B Value</Label>
                  <Input value={abTestForm.variant_b_value} onChange={(e) => setAbTestForm({ ...abTestForm, variant_b_value: e.target.value })} className="bg-black/50 border-white/10" placeholder="e.g., $69.99" />
                </div>
              </div>
              <div>
                <Label>Tracking Metric</Label>
                <Select value={abTestForm.metric} onValueChange={(v) => setAbTestForm({ ...abTestForm, metric: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="conversion_rate">Conversion Rate</SelectItem>
                    <SelectItem value="click_through">Click-Through Rate</SelectItem>
                    <SelectItem value="add_to_cart">Add to Cart Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-purple-500 text-black font-bold uppercase" data-testid="save-ab-test-button">
                <Save className="w-4 h-4 mr-2" />Create Test
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    {abTests.length === 0 ? (
      <div className="glass p-8 text-center">
        <FlaskConical className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No A/B tests created yet</p>
      </div>
    ) : (
      <div className="space-y-4">
        {abTests.map((test) => {
          const aRate = test.variant_a_views > 0 ? ((test.variant_a_conversions / test.variant_a_views) * 100).toFixed(1) : '0.0';
          const bRate = test.variant_b_views > 0 ? ((test.variant_b_conversions / test.variant_b_views) * 100).toFixed(1) : '0.0';
          const currentLeader = parseFloat(aRate) > parseFloat(bRate) ? "a" : parseFloat(bRate) > parseFloat(aRate) ? "b" : null;
          const confidence = test.confidence || 0;
          const isCompleted = test.status === "completed";
          const declaredWinner = test.winner;
          return (
            <div key={test.test_id} className={`glass p-5 ${isCompleted ? 'border border-neon-green/30' : ''}`} data-testid={`ab-test-${test.test_id}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{test.name}</p>
                    {isCompleted && declaredWinner && (
                      <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 text-xs" data-testid={`ab-winner-badge-${test.test_id}`}>
                        <Trophy className="w-3 h-3 mr-1" />Winner: Variant {declaredWinner.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">Item: {test.merch_name} | Metric: {test.metric?.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Confidence indicator */}
                  <div className="hidden sm:flex items-center gap-2" data-testid={`ab-confidence-${test.test_id}`}>
                    <div className="w-20 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${confidence >= 95 ? 'bg-neon-green' : confidence >= 80 ? 'bg-yellow-400' : 'bg-zinc-600'}`}
                        style={{ width: `${Math.min(confidence, 100)}%` }}
                      />
                    </div>
                    <span className={`text-xs font-mono ${confidence >= 95 ? 'text-neon-green' : confidence >= 80 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                      {confidence.toFixed(0)}%
                    </span>
                  </div>
                  <Badge className={test.status === 'active' ? 'bg-neon-green/20 text-neon-green border-neon-green/50' : test.status === 'paused' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' : 'bg-purple-500/20 text-purple-400 border-purple-500/50'}>
                    {test.status}
                  </Badge>
                  {!isCompleted && (
                    <Button variant="ghost" size="sm" onClick={() => handleToggleAbTest(test.test_id, test.status)} className="text-xs">
                      {test.status === 'active' ? 'Pause' : 'Resume'}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteAbTest(test.test_id)} className="text-zinc-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`bg-white/5 p-4 border ${declaredWinner === "a" ? "border-neon-green/50 bg-neon-green/5" : currentLeader === "a" && !isCompleted ? "border-neon-cyan/30" : "border-white/10"}`}>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Variant A: {test.variant_a_label}</p>
                  <p className="text-sm text-zinc-300 mb-2">{test.variant_a_value}</p>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Views: {test.variant_a_views}</span>
                    <span className="text-zinc-500">Conv: {test.variant_a_conversions}</span>
                    <span className={`font-bold ${declaredWinner === "a" ? "text-neon-green" : "text-neon-cyan"}`}>{aRate}%</span>
                  </div>
                  {declaredWinner === "a" && <Badge className="mt-2 bg-neon-green/20 text-neon-green border-neon-green/50 text-xs"><Trophy className="w-3 h-3 mr-1" />Winner</Badge>}
                  {!isCompleted && currentLeader === "a" && <Badge className="mt-2 bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 text-xs">Leading</Badge>}
                </div>
                <div className={`bg-white/5 p-4 border ${declaredWinner === "b" ? "border-neon-green/50 bg-neon-green/5" : currentLeader === "b" && !isCompleted ? "border-purple-500/30" : "border-purple-500/20"}`}>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Variant B: {test.variant_b_label}</p>
                  <p className="text-sm text-zinc-300 mb-2">{test.variant_b_value}</p>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Views: {test.variant_b_views}</span>
                    <span className="text-zinc-500">Conv: {test.variant_b_conversions}</span>
                    <span className={`font-bold ${declaredWinner === "b" ? "text-neon-green" : "text-purple-400"}`}>{bRate}%</span>
                  </div>
                  {declaredWinner === "b" && <Badge className="mt-2 bg-neon-green/20 text-neon-green border-neon-green/50 text-xs"><Trophy className="w-3 h-3 mr-1" />Winner</Badge>}
                  {!isCompleted && currentLeader === "b" && <Badge className="mt-2 bg-purple-500/20 text-purple-400 border-purple-500/50 text-xs">Leading</Badge>}
                </div>
              </div>
              {/* Confidence summary row */}
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                <span>Total views: {(test.variant_a_views || 0) + (test.variant_b_views || 0)}</span>
                <span className="sm:hidden">Confidence: <span className={confidence >= 95 ? 'text-neon-green' : confidence >= 80 ? 'text-yellow-400' : 'text-zinc-400'}>{confidence.toFixed(0)}%</span></span>
                {isCompleted && <span className="text-neon-green">Auto-declared winner at {'\u2265'}95% confidence</span>}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const RewardsTab = ({ overview, users, showAwardDialog, setShowAwardDialog, awardForm, setAwardForm, handleAwardPoints, showBonusEventDialog, setShowBonusEventDialog, bonusEventForm, setBonusEventForm, handleCreateBonusEvent, handleDeleteBonusEvent }) => {
  if (!overview) return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto text-zinc-500" /></div>;

  const tierColors = { rookie: "text-zinc-400", regular: "text-neon-cyan", vip: "text-neon-magenta", legend: "text-neon-yellow" };

  return (
    <div data-testid="rewards-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl uppercase">Loyalty & Rewards</h2>
        <div className="flex gap-2">
          <Dialog open={showBonusEventDialog} onOpenChange={setShowBonusEventDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-neon-yellow/50 text-neon-yellow" data-testid="create-bonus-event-button">
                <Zap className="w-4 h-4 mr-1" /> Bonus Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Create Bonus Points Event</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateBonusEvent} className="space-y-4">
                <div><Label>Event Name</Label><Input value={bonusEventForm.name} onChange={(e) => setBonusEventForm({...bonusEventForm, name: e.target.value})} className="bg-black/50 border-white/10" required placeholder="e.g., 2x Points Weekend" /></div>
                <div><Label>Multiplier</Label><Input type="number" step="0.5" value={bonusEventForm.multiplier} onChange={(e) => setBonusEventForm({...bonusEventForm, multiplier: parseFloat(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Starts</Label><Input type="datetime-local" value={bonusEventForm.starts_at} onChange={(e) => setBonusEventForm({...bonusEventForm, starts_at: new Date(e.target.value).toISOString()})} className="bg-black/50 border-white/10" required /></div>
                  <div><Label>Ends</Label><Input type="datetime-local" value={bonusEventForm.ends_at} onChange={(e) => setBonusEventForm({...bonusEventForm, ends_at: new Date(e.target.value).toISOString()})} className="bg-black/50 border-white/10" required /></div>
                </div>
                <div><Label>Description</Label><Input value={bonusEventForm.description} onChange={(e) => setBonusEventForm({...bonusEventForm, description: e.target.value})} className="bg-black/50 border-white/10" /></div>
                <Button type="submit" className="w-full bg-neon-yellow text-black font-bold"><Zap className="w-4 h-4 mr-2" />Create Event</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showAwardDialog} onOpenChange={setShowAwardDialog}>
            <DialogTrigger asChild>
              <Button className="bg-neon-yellow text-black" data-testid="award-points-button">
                <Gift className="w-4 h-4 mr-1" /> Award Points
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Award Bonus Points</DialogTitle></DialogHeader>
              <form onSubmit={handleAwardPoints} className="space-y-4">
                <div>
                  <Label>User</Label>
                  <Select value={awardForm.user_id} onValueChange={(v) => setAwardForm({...awardForm, user_id: v})}>
                    <SelectTrigger className="bg-black/50 border-white/10"><SelectValue placeholder="Select user..." /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                      {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Points</Label><Input type="number" value={awardForm.points} onChange={(e) => setAwardForm({...awardForm, points: parseInt(e.target.value)})} className="bg-black/50 border-white/10" /></div>
                <div><Label>Reason</Label><Input value={awardForm.reason} onChange={(e) => setAwardForm({...awardForm, reason: e.target.value})} className="bg-black/50 border-white/10" /></div>
                <Button type="submit" className="w-full bg-neon-yellow text-black font-bold"><Gift className="w-4 h-4 mr-2" />Award Points</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="glass p-5 text-center">
          <p className="font-heading text-3xl text-neon-yellow">{overview.total_members}</p>
          <p className="text-xs text-zinc-500 uppercase mt-1">Members</p>
        </div>
        <div className="glass p-5 text-center">
          <p className="font-heading text-3xl text-neon-cyan">{overview.total_outstanding_points?.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 uppercase mt-1">Outstanding Points</p>
        </div>
        <div className="glass p-5 text-center">
          <p className="font-heading text-3xl text-neon-green">${overview.outstanding_value?.toFixed(2)}</p>
          <p className="text-xs text-zinc-500 uppercase mt-1">Outstanding Value</p>
        </div>
        <div className="glass p-5 text-center">
          <p className="font-heading text-3xl text-neon-magenta">{overview.total_lifetime_points?.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 uppercase mt-1">Lifetime Points</p>
        </div>
      </div>

      {/* Tier Distribution */}
      <div className="glass p-5 mb-6">
        <h3 className="font-heading text-sm uppercase tracking-wide text-zinc-400 mb-4">Tier Distribution</h3>
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(overview.tier_distribution || {}).map(([tier, count]) => (
            <div key={tier} className="text-center p-3 bg-white/5 rounded-sm">
              <p className={`font-heading text-2xl ${tierColors[tier]}`}>{count}</p>
              <p className="text-xs text-zinc-500 uppercase">{tier}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Bonus Events */}
      {overview.active_events?.length > 0 && (
        <div className="glass p-5 mb-6">
          <h3 className="font-heading text-sm uppercase tracking-wide text-zinc-400 mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-neon-yellow" /> Active Bonus Events
          </h3>
          {overview.active_events.map(evt => (
            <div key={evt.event_id} className="flex items-center justify-between p-3 bg-neon-yellow/10 border border-neon-yellow/30 mb-2">
              <div>
                <p className="font-medium text-neon-yellow">{evt.name}</p>
                <p className="text-xs text-zinc-500">{evt.multiplier}x multiplier | {evt.description}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDeleteBonusEvent(evt.event_id)} className="text-zinc-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Top Point Holders */}
      <div className="glass p-5">
        <h3 className="font-heading text-sm uppercase tracking-wide text-zinc-400 mb-4">Top Members</h3>
        {overview.top_users?.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No members yet</p>
        ) : (
          <div className="space-y-2">
            {overview.top_users?.map((u, idx) => (
              <div key={u.user_id} className="flex items-center justify-between p-3 bg-white/5 rounded-sm">
                <div className="flex items-center gap-3">
                  <span className="text-zinc-600 font-mono text-sm w-6">#{idx+1}</span>
                  <div>
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-zinc-600">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={`${u.tier?.tier === 'legend' ? 'bg-neon-yellow/20 text-neon-yellow border-neon-yellow/50' : u.tier?.tier === 'vip' ? 'bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50' : u.tier?.tier === 'regular' ? 'bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'} text-xs uppercase`}>
                    {u.tier?.name}
                  </Badge>
                  <span className="font-mono text-neon-yellow text-sm font-bold">{u.lifetime_points?.toLocaleString()} pts</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== BUNDLES TAB =====================
const BundlesTab = ({ bundles, beats, showBundleDialog, setShowBundleDialog, bundleForm, setBundleForm, handleCreateBundle, handleDeleteBundle }) => {
  const [selectedBeats, setSelectedBeats] = useState([]);

  const toggleBeat = (beatId) => {
    const newIds = selectedBeats.includes(beatId)
      ? selectedBeats.filter(id => id !== beatId)
      : [...selectedBeats, beatId];
    setSelectedBeats(newIds);
    setBundleForm({ ...bundleForm, beat_ids: newIds });
  };

  return (
    <div data-testid="bundles-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl uppercase">Beat Bundles</h2>
        <Dialog open={showBundleDialog} onOpenChange={(open) => { setShowBundleDialog(open); if (open) setSelectedBeats([]); }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-500 text-white" data-testid="create-bundle-button">
              <Plus className="w-4 h-4 mr-2" />New Bundle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create Beat Bundle</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateBundle} className="space-y-4">
              <div>
                <Label>Bundle Name</Label>
                <Input value={bundleForm.name} onChange={(e) => setBundleForm({ ...bundleForm, name: e.target.value })} className="bg-black/50 border-white/10" required placeholder="Trap Starter Pack" data-testid="bundle-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} className="bg-black/50 border-white/10" placeholder="5 hard-hitting trap beats..." />
              </div>
              <div>
                <Label>Bundle Price ($)</Label>
                <Input type="number" step="0.01" value={bundleForm.price} onChange={(e) => setBundleForm({ ...bundleForm, price: parseFloat(e.target.value) || 0 })} className="bg-black/50 border-white/10" required data-testid="bundle-price-input" />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={bundleForm.image_url} onChange={(e) => setBundleForm({ ...bundleForm, image_url: e.target.value })} className="bg-black/50 border-white/10" placeholder="https://..." />
              </div>
              <div>
                <Label>Select Beats ({selectedBeats.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto space-y-1 mt-2 bg-black/30 p-2 border border-white/5">
                  {beats.map((beat) => (
                    <label key={beat.beat_id} className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-white/5 ${selectedBeats.includes(beat.beat_id) ? "bg-purple-500/20" : ""}`}>
                      <input type="checkbox" checked={selectedBeats.includes(beat.beat_id)} onChange={() => toggleBeat(beat.beat_id)} className="accent-purple-500" />
                      <span className="text-sm truncate">{beat.title}</span>
                      <span className="text-xs text-zinc-500 ml-auto">${beat.basic_price}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full bg-purple-500 text-white font-bold" data-testid="save-bundle-button">
                <Save className="w-4 h-4 mr-2" />Create Bundle
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {bundles.length === 0 ? (
        <div className="glass p-8 text-center">
          <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No bundles created yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bundles.map((bundle) => (
            <div key={bundle.bundle_id} className="glass p-5 flex items-center gap-4" data-testid={`admin-bundle-${bundle.bundle_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium">{bundle.name}</p>
                  <Badge className={bundle.active ? "bg-neon-green/20 text-neon-green" : "bg-zinc-500/20 text-zinc-400"}>
                    {bundle.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-xs text-zinc-500">{bundle.beat_ids?.length || 0} beats | {bundle.purchases || 0} purchases</p>
              </div>
              <div className="text-right mr-4">
                <p className="font-heading text-xl text-neon-green">${bundle.price?.toFixed(2)}</p>
                {bundle.original_price > bundle.price && (
                  <p className="text-xs text-zinc-500 line-through">${bundle.original_price?.toFixed(2)}</p>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDeleteBundle(bundle.bundle_id)} className="text-zinc-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== FLASH SALES TAB =====================
const FlashSalesTab = ({ sales, showFlashSaleDialog, setShowFlashSaleDialog, flashSaleForm, setFlashSaleForm, handleCreateFlashSale, handleDeleteFlashSale }) => (
  <div data-testid="flash-sales-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Flash Sales</h2>
      <Dialog open={showFlashSaleDialog} onOpenChange={setShowFlashSaleDialog}>
        <DialogTrigger asChild>
          <Button className="bg-red-500 text-white" data-testid="create-flash-sale-button">
            <Zap className="w-4 h-4 mr-2" />New Flash Sale
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Create Flash Sale</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateFlashSale} className="space-y-4">
            <div>
              <Label>Sale Name</Label>
              <Input value={flashSaleForm.name} onChange={(e) => setFlashSaleForm({ ...flashSaleForm, name: e.target.value })} className="bg-black/50 border-white/10" required placeholder="Weekend Beat Blowout" data-testid="flash-sale-name-input" />
            </div>
            <div>
              <Label>Discount Percentage (%)</Label>
              <Input type="number" min="1" max="90" value={flashSaleForm.discount_percent} onChange={(e) => setFlashSaleForm({ ...flashSaleForm, discount_percent: parseFloat(e.target.value) || 0 })} className="bg-black/50 border-white/10" required data-testid="flash-sale-discount-input" />
            </div>
            <div>
              <Label>Applies To</Label>
              <Select value={flashSaleForm.applies_to} onValueChange={(v) => setFlashSaleForm({ ...flashSaleForm, applies_to: v })}>
                <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="beats">Beats Only</SelectItem>
                  <SelectItem value="merch">Merch Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input type="datetime-local" value={flashSaleForm.start_time?.slice(0, 16)} onChange={(e) => setFlashSaleForm({ ...flashSaleForm, start_time: new Date(e.target.value).toISOString() })} className="bg-black/50 border-white/10" required data-testid="flash-sale-start" />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="datetime-local" value={flashSaleForm.end_time?.slice(0, 16)} onChange={(e) => setFlashSaleForm({ ...flashSaleForm, end_time: new Date(e.target.value).toISOString() })} className="bg-black/50 border-white/10" required data-testid="flash-sale-end" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-500 text-white font-bold" data-testid="save-flash-sale-button">
              <Zap className="w-4 h-4 mr-2" />Launch Flash Sale
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>

    {sales.length === 0 ? (
      <div className="glass p-8 text-center">
        <Zap className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No flash sales created yet</p>
      </div>
    ) : (
      <div className="space-y-4">
        {sales.map((sale) => (
          <div key={sale.sale_id} className="glass p-5" data-testid={`admin-flash-sale-${sale.sale_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium">{sale.name}</p>
                  <Badge className={sale.is_live ? "bg-red-500/20 text-red-400 animate-pulse" : sale.active ? "bg-amber-500/20 text-amber-400" : "bg-zinc-500/20 text-zinc-400"}>
                    {sale.is_live ? "LIVE" : sale.active ? "Scheduled" : "Ended"}
                  </Badge>
                  <Badge className="bg-white/5 text-zinc-300">{sale.discount_percent}% OFF</Badge>
                  <Badge className="bg-white/5 text-zinc-500">{sale.applies_to}</Badge>
                </div>
                <p className="text-xs text-zinc-500">
                  {new Date(sale.start_time).toLocaleString()} - {new Date(sale.end_time).toLocaleString()}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDeleteFlashSale(sale.sale_id)} className="text-zinc-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ===================== GIFT CARDS TAB =====================
const GiftCardsTab = ({ data }) => (
  <div data-testid="gift-cards-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Gift Cards</h2>
      <div className="flex gap-4">
        <div className="glass px-4 py-2 text-center">
          <p className="font-heading text-lg text-neon-green">${data.total_sold?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-zinc-500">Total Sold</p>
        </div>
        <div className="glass px-4 py-2 text-center">
          <p className="font-heading text-lg text-yellow-400">${data.total_redeemed?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-zinc-500">Total Redeemed</p>
        </div>
      </div>
    </div>

    {(!data.cards || data.cards.length === 0) ? (
      <div className="glass p-8 text-center">
        <Gift className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No gift cards purchased yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {data.cards.map((card) => (
          <div key={card.card_id} className="glass p-4 flex items-center gap-4" data-testid={`admin-gc-${card.card_id}`}>
            <Gift className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm tracking-wider text-yellow-400">{card.code}</p>
              <p className="text-xs text-zinc-500 truncate">
                {card.recipient_email ? `To: ${card.recipient_email}` : "Self-purchase"}
              </p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg">${card.amount.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">Amount</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg text-neon-green">${card.balance.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">Balance</p>
            </div>
            <Badge className={card.status === "active" ? "bg-neon-green/20 text-neon-green" : "bg-zinc-500/20 text-zinc-400"}>
              {card.status}
            </Badge>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ===================== CAR SUBMISSIONS TAB =====================
const CarSubmissionsTab = ({ submissions, handleApprove, handleReject }) => {
  const pending = submissions.filter(s => s.status === "pending");
  const resolved = submissions.filter(s => s.status !== "pending");
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div data-testid="submissions-admin">
      <h2 className="font-heading text-xl uppercase mb-6">User Submissions</h2>

      {pending.length === 0 && resolved.length === 0 ? (
        <div className="glass p-8 text-center">
          <Upload className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No submissions yet</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm uppercase text-amber-400 tracking-wide mb-3">Pending Review ({pending.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pending.map((sub) => (
                  <div key={sub.submission_id} className="glass overflow-hidden" data-testid={`submission-${sub.submission_id}`}>
                    <div className="aspect-video relative">
                      <img src={resolveUrl(sub.media_url)} alt={sub.title} className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' fill='%2318181b'%3E%3Crect width='400' height='225'/%3E%3C/svg%3E"; }} />
                      <Badge className="absolute top-2 left-2 bg-amber-500 text-black font-bold">PENDING</Badge>
                    </div>
                    <div className="p-4">
                      <p className="font-medium">{sub.title}</p>
                      <p className="text-xs text-zinc-500 mb-1">by {sub.user_name} | {sub.media_type}</p>
                      <p className="text-xs text-zinc-600 mb-3 line-clamp-2">{sub.description}</p>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApprove(sub.submission_id)}
                          className="flex-1 bg-neon-green text-black font-bold" data-testid={`approve-${sub.submission_id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleReject(sub.submission_id)}
                          className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`reject-${sub.submission_id}`}>
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {resolved.length > 0 && (
            <div>
              <h3 className="text-sm uppercase text-zinc-500 tracking-wide mb-3">History ({resolved.length})</h3>
              <div className="space-y-2">
                {resolved.map((sub) => (
                  <div key={sub.submission_id} className="glass p-3 flex items-center gap-3">
                    <div className="w-12 h-12 bg-zinc-800 flex-shrink-0 overflow-hidden">
                      <img src={resolveUrl(sub.thumbnail_url || sub.media_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{sub.title} <span className="text-zinc-600">by {sub.user_name}</span></p>
                    </div>
                    <Badge className={sub.status === "approved" ? "bg-neon-green/20 text-neon-green" : "bg-red-500/20 text-red-400"}>
                      {sub.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Producers Admin Tab
const ProducersAdminTab = ({ producers, producerBeats, handleApproveProducer, handleRejectProducer, handleApproveProducerBeat, handleRejectProducerBeat }) => {
  const pendingProducers = producers.filter(p => p.status === "pending");
  const approvedProducers = producers.filter(p => p.status === "approved");
  const pendingBeats = producerBeats.filter(b => b.status === "pending");
  const processedBeats = producerBeats.filter(b => b.status !== "pending");

  return (
    <div data-testid="producers-admin">
      <h2 className="font-heading text-xl uppercase mb-6">Producer Management</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-cyan">{producers.length}</p>
          <p className="text-xs text-zinc-500 uppercase">Total Applications</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-amber-400">{pendingProducers.length}</p>
          <p className="text-xs text-zinc-500 uppercase">Pending</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-green">{approvedProducers.length}</p>
          <p className="text-xs text-zinc-500 uppercase">Approved</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-magenta">{producerBeats.length}</p>
          <p className="text-xs text-zinc-500 uppercase">Beat Submissions</p>
        </div>
      </div>

      {/* Pending Producer Applications */}
      {pendingProducers.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm uppercase text-amber-400 tracking-wide mb-3">Pending Applications ({pendingProducers.length})</h3>
          <div className="space-y-3">
            {pendingProducers.map((p) => (
              <div key={p.producer_id} className="glass p-4" data-testid={`producer-app-${p.producer_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neon-cyan">{p.artist_name}</p>
                    <p className="text-xs text-zinc-500">{p.user_email} | Applied: {new Date(p.created_at).toLocaleDateString()}</p>
                    {p.bio && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{p.bio}</p>}
                    {p.portfolio_url && <a href={p.portfolio_url} target="_blank" rel="noreferrer" className="text-xs text-neon-cyan hover:underline mt-1 inline-block"><Link2 className="w-3 h-3 inline mr-1" />Portfolio</a>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleApproveProducer(p.producer_id)}
                      className="bg-neon-green text-black font-bold" data-testid={`approve-producer-${p.producer_id}`}>
                      <CheckCircle className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleRejectProducer(p.producer_id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`reject-producer-${p.producer_id}`}>
                      <XCircle className="w-4 h-4 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Beat Submissions */}
      {pendingBeats.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm uppercase text-orange-400 tracking-wide mb-3">Pending Beat Submissions ({pendingBeats.length})</h3>
          <div className="space-y-3">
            {pendingBeats.map((b) => (
              <div key={b.submission_id} className="glass p-4 flex items-center gap-4" data-testid={`producer-beat-sub-${b.submission_id}`}>
                <Music className="w-5 h-5 text-neon-cyan flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{b.title}</p>
                  <p className="text-xs text-zinc-500">by {b.producer_name} | {b.genre} | {b.bpm} BPM | {b.key}</p>
                  <p className="text-xs text-zinc-600">Prices: ${b.basic_price} / ${b.premium_price} / ${b.unlimited_price} / ${b.exclusive_price}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApproveProducerBeat(b.submission_id)}
                    className="bg-neon-green text-black font-bold" data-testid={`approve-beat-${b.submission_id}`}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRejectProducerBeat(b.submission_id)}
                    className="border-red-500/50 text-red-400" data-testid={`reject-beat-${b.submission_id}`}>
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Producers List */}
      {approvedProducers.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm uppercase text-neon-green tracking-wide mb-3">Active Producers ({approvedProducers.length})</h3>
          <div className="space-y-2">
            {approvedProducers.map((p) => (
              <div key={p.producer_id} className="glass p-3 flex items-center gap-3" data-testid={`active-producer-${p.producer_id}`}>
                <Mic2 className="w-5 h-5 text-neon-green" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{p.artist_name} <span className="text-zinc-600 text-xs">({p.user_email})</span></p>
                </div>
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 text-xs">{p.revenue_split || 50}% split</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Beat History */}
      {processedBeats.length > 0 && (
        <div>
          <h3 className="text-sm uppercase text-zinc-500 tracking-wide mb-3">Beat History ({processedBeats.length})</h3>
          <div className="space-y-2">
            {processedBeats.map((b) => (
              <div key={b.submission_id} className="glass p-3 flex items-center gap-3">
                <Music className="w-4 h-4 text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{b.title} <span className="text-zinc-600">by {b.producer_name}</span></p>
                </div>
                <Badge className={b.status === "approved" ? "bg-neon-green/20 text-neon-green" : "bg-red-500/20 text-red-400"}>
                  {b.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {producers.length === 0 && producerBeats.length === 0 && (
        <div className="glass p-8 text-center">
          <Mic2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No producer applications yet</p>
        </div>
      )}
    </div>
  );
};

// Tickets Admin Tab
const TicketsAdminTab = ({ tickets, handleUpdateStatus, handleReply }) => {
  const [replyText, setReplyText] = useState({});
  const openTickets = tickets.filter(t => t.status === "open" || t.status === "in-progress");
  const closedTickets = tickets.filter(t => t.status === "resolved" || t.status === "closed");

  return (
    <div data-testid="tickets-admin">
      <h2 className="font-heading text-xl uppercase mb-2">Support Tickets</h2>
      <p className="text-xs text-zinc-500 mb-6">{openTickets.length} open, {closedTickets.length} resolved</p>

      {tickets.length === 0 ? (
        <div className="glass p-8 text-center">
          <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No support tickets yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {openTickets.length > 0 && <h3 className="text-sm uppercase text-sky-400 tracking-wide">Open ({openTickets.length})</h3>}
          {openTickets.map((ticket) => (
            <div key={ticket.ticket_id} className="glass p-5" data-testid={`admin-ticket-${ticket.ticket_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium">{ticket.subject}</p>
                  <p className="text-xs text-zinc-500">{ticket.user_name} ({ticket.user_email}) | {ticket.category} | {new Date(ticket.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={ticket.status === 'open' ? 'bg-sky-500/20 text-sky-400' : 'bg-amber-500/20 text-amber-400'}>{ticket.status}</Badge>
                  <Select defaultValue={ticket.status} onValueChange={(v) => handleUpdateStatus(ticket.ticket_id, v)}>
                    <SelectTrigger className="bg-black/50 border-white/10 w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Latest message */}
              {ticket.messages?.length > 0 && (
                <div className="bg-white/5 p-3 rounded-sm mb-3 text-sm text-zinc-300">
                  <span className="text-xs text-zinc-500 block mb-1">
                    {ticket.messages[ticket.messages.length - 1].sender === 'admin' ? 'You' : ticket.user_name}:
                  </span>
                  {ticket.messages[ticket.messages.length - 1].message}
                </div>
              )}
              {/* Quick reply */}
              <div className="flex gap-2">
                <Input
                  value={replyText[ticket.ticket_id] || ""}
                  onChange={(e) => setReplyText({ ...replyText, [ticket.ticket_id]: e.target.value })}
                  placeholder="Quick reply..."
                  className="bg-black/50 border-white/10 flex-1 h-8 text-sm"
                />
                <Button
                  size="sm"
                  disabled={!replyText[ticket.ticket_id]?.trim()}
                  onClick={() => {
                    handleReply(ticket.ticket_id, replyText[ticket.ticket_id]);
                    setReplyText({ ...replyText, [ticket.ticket_id]: "" });
                  }}
                  className="bg-neon-cyan text-black h-8"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}

          {closedTickets.length > 0 && (
            <>
              <h3 className="text-sm uppercase text-zinc-500 tracking-wide mt-6">Resolved ({closedTickets.length})</h3>
              {closedTickets.slice(0, 10).map((ticket) => (
                <div key={ticket.ticket_id} className="glass p-3 flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-neon-green flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{ticket.subject} <span className="text-zinc-600">- {ticket.user_name}</span></p>
                  </div>
                  <Badge className="bg-neon-green/20 text-neon-green text-xs">{ticket.status}</Badge>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Sound Kits Admin Tab
const SoundKitsAdminTab = ({ kits, showDialog, setShowDialog, form, setForm, handleCreate, handleDelete }) => (
  <div data-testid="soundkits-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Sound Kits & Samples</h2>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogTrigger asChild>
          <Button className="bg-orange-500 text-black" data-testid="create-soundkit-button">
            <Plus className="w-4 h-4 mr-2" />New Sound Kit
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Create Sound Kit</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/50 border-white/10" required placeholder="Tokyo Trap Drums" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/50 border-white/10" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="drum_kit">Drum Kit</SelectItem>
                    <SelectItem value="sample_pack">Sample Pack</SelectItem>
                    <SelectItem value="loop_kit">Loop Kit</SelectItem>
                    <SelectItem value="preset_pack">Preset Pack</SelectItem>
                    <SelectItem value="one_shot">One Shot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price ($)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-black/50 border-white/10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sample Count</Label>
                <Input type="number" value={form.sample_count} onChange={(e) => setForm({ ...form, sample_count: parseInt(e.target.value) || 0 })} className="bg-black/50 border-white/10" />
              </div>
              <div>
                <Label>Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="WAV">WAV</SelectItem>
                    <SelectItem value="MP3">MP3</SelectItem>
                    <SelectItem value="MIDI">MIDI</SelectItem>
                    <SelectItem value="Mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-black/50 border-white/10" placeholder="https://..." />
            </div>
            <div>
              <Label>Tags (comma separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-black/50 border-white/10" placeholder="trap, drums, 808" />
            </div>
            <Button type="submit" className="w-full bg-orange-500 text-black font-bold uppercase" data-testid="save-soundkit-button">
              <Save className="w-4 h-4 mr-2" />Create Sound Kit
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>

    {kits.length === 0 ? (
      <div className="glass p-8 text-center">
        <Layers className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No sound kits created yet</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kits.map((kit) => (
          <div key={kit.kit_id} className="glass p-4 flex gap-4" data-testid={`admin-kit-${kit.kit_id}`}>
            <div className="w-20 h-20 bg-zinc-800 flex-shrink-0 overflow-hidden rounded-sm">
              {kit.image_url && <img src={kit.image_url} alt={kit.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{kit.name}</p>
              <p className="text-xs text-zinc-500">{kit.category} | {kit.sample_count} samples | {kit.format}</p>
              <p className="text-orange-400 font-mono mt-1">${kit.price?.toFixed(2)}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => handleDelete(kit.kit_id)} className="text-zinc-400 hover:text-red-500 flex-shrink-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Advanced Analytics Tab
const AdvancedAnalyticsTab = ({ data }) => {
  if (!data) return (
    <div className="glass p-8 text-center">
      <Loader2 className="w-8 h-8 animate-spin text-neon-cyan mx-auto mb-3" />
      <p className="text-zinc-500">Loading analytics...</p>
    </div>
  );

  const rb = data.revenue_breakdown || {};

  return (
    <div data-testid="advanced-analytics-admin">
      <h2 className="font-heading text-xl uppercase mb-6">Advanced Analytics</h2>

      {/* Revenue Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: "Total Revenue", value: `$${rb.total?.toFixed(2)}`, color: "text-neon-green" },
          { label: "Beats", value: `$${rb.beats?.toFixed(2)}`, color: "text-neon-cyan" },
          { label: "Merch", value: `$${rb.merch?.toFixed(2)}`, color: "text-neon-magenta" },
          { label: "Sound Kits", value: `$${rb.soundkits?.toFixed(2)}`, color: "text-orange-400" },
          { label: "Bundles", value: `$${rb.bundles?.toFixed(2)}`, color: "text-purple-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="glass p-4 text-center">
            <p className={`font-heading text-xl ${color}`}>{value}</p>
            <p className="text-xs text-zinc-500 uppercase">{label}</p>
          </div>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-cyan">${data.avg_ltv?.toFixed(2)}</p>
          <p className="text-xs text-zinc-500 uppercase">Avg Customer LTV</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-white">{data.total_customers}</p>
          <p className="text-xs text-zinc-500 uppercase">Total Customers</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-green">{data.new_users_30d}</p>
          <p className="text-xs text-zinc-500 uppercase">New Users (30d)</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-yellow">{data.total_orders}</p>
          <p className="text-xs text-zinc-500 uppercase">Total Orders</p>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      {data.monthly_revenue?.length > 0 && (
        <div className="glass p-6 mb-8">
          <h3 className="text-sm uppercase text-zinc-400 mb-4">Monthly Revenue</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip contentStyle={{ background: '#09090b', border: '1px solid rgba(255,255,255,0.1)' }} />
                <Bar dataKey="revenue" fill="#00f3ff" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Customers */}
        {data.top_customers?.length > 0 && (
          <div className="glass p-6">
            <h3 className="text-sm uppercase text-zinc-400 mb-4">Top Customers by Spend</h3>
            <div className="space-y-2">
              {data.top_customers.map((c, i) => (
                <div key={c.user_id} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                  <span className="text-xs font-mono text-zinc-600 w-6">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{c.name}</p>
                    <p className="text-[10px] text-zinc-600">{c.email}</p>
                  </div>
                  <span className="text-neon-green font-mono text-sm">${c.total_spend?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Beats */}
        {data.top_beats?.length > 0 && (
          <div className="glass p-6">
            <h3 className="text-sm uppercase text-zinc-400 mb-4">Top Selling Beats</h3>
            <div className="space-y-2">
              {data.top_beats.map((b, i) => (
                <div key={b.beat_id} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                  <span className="text-xs font-mono text-zinc-600 w-6">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{b.title}</p>
                    <p className="text-[10px] text-zinc-600">{b.sales} sales</p>
                  </div>
                  <span className="text-neon-cyan font-mono text-sm">${b.revenue?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Merch */}
        {data.top_merch?.length > 0 && (
          <div className="glass p-6">
            <h3 className="text-sm uppercase text-zinc-400 mb-4">Top Selling Merch</h3>
            <div className="space-y-2">
              {data.top_merch.map((m, i) => (
                <div key={m.merch_id} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                  <span className="text-xs font-mono text-zinc-600 w-6">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{m.name}</p>
                    <p className="text-[10px] text-zinc-600">{m.sales} sales</p>
                  </div>
                  <span className="text-neon-magenta font-mono text-sm">${m.revenue?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Subscriptions Admin Tab
const SubscriptionsAdminTab = ({ data }) => {
  if (!data) return (
    <div className="glass p-8 text-center">
      <Loader2 className="w-8 h-8 animate-spin text-neon-cyan mx-auto mb-3" />
      <p className="text-zinc-500">Loading...</p>
    </div>
  );
  return (
    <div data-testid="subs-admin">
      <h2 className="font-heading text-xl uppercase mb-6">Beat Club Subscriptions</h2>
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-green">{data.active_count}</p>
          <p className="text-xs text-zinc-500 uppercase">Active</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-white">{data.subscriptions?.length}</p>
          <p className="text-xs text-zinc-500 uppercase">Total</p>
        </div>
        <div className="glass p-4 text-center">
          <p className="font-heading text-2xl text-neon-yellow">${data.total_revenue?.toFixed(2)}</p>
          <p className="text-xs text-zinc-500 uppercase">Revenue</p>
        </div>
      </div>
      {data.subscriptions?.length === 0 ? (
        <div className="glass p-8 text-center"><Crown className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-500">No subscriptions yet</p></div>
      ) : (
        <div className="space-y-2">
          {data.subscriptions?.map((sub) => (
            <div key={sub.subscription_id} className="glass p-4 flex items-center gap-4" data-testid={`sub-${sub.subscription_id}`}>
              <Crown className={`w-5 h-5 ${sub.status === 'active' ? 'text-neon-yellow' : 'text-zinc-600'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm">{sub.user_id}</p>
                <p className="text-xs text-zinc-500">{sub.plan} | ${sub.price} | Renews: {new Date(sub.next_renewal).toLocaleDateString()}</p>
              </div>
              <Badge className={sub.status === 'active' ? 'bg-neon-green/20 text-neon-green' : 'bg-red-500/20 text-red-400'}>{sub.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Contests Admin Tab
const ContestsAdminTab = ({ contests, showDialog, setShowDialog, form, setForm, handleCreate, handleUpdateStatus }) => (
  <div data-testid="contests-admin">
    <div className="flex justify-between items-center mb-6">
      <h2 className="font-heading text-xl uppercase">Beat Contests</h2>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogTrigger asChild>
          <Button className="bg-amber-500 text-black" data-testid="create-contest-button">
            <Plus className="w-4 h-4 mr-2" />New Contest
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Create Contest</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black/50 border-white/10" required placeholder="Beat Battle #1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/50 border-white/10" rows={3} />
            </div>
            <div>
              <Label>Prize</Label>
              <Input value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="bg-black/50 border-white/10" placeholder="$100 store credit + featured placement" />
            </div>
            <div>
              <Label>Genre</Label>
              <Input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="bg-black/50 border-white/10" placeholder="any" />
            </div>
            <Button type="submit" className="w-full bg-amber-500 text-black font-bold uppercase" data-testid="save-contest-button">
              <Save className="w-4 h-4 mr-2" />Create Contest
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    {contests.length === 0 ? (
      <div className="glass p-8 text-center"><Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-500">No contests created yet</p></div>
    ) : (
      <div className="space-y-3">
        {contests.map((c) => (
          <div key={c.contest_id} className="glass p-5" data-testid={`admin-contest-${c.contest_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{c.title}</p>
                <p className="text-xs text-zinc-500">{c.genre} | Deadline: {new Date(c.deadline).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={c.status === 'open' ? 'bg-neon-green/20 text-neon-green' : c.status === 'closed' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                  {c.status}
                </Badge>
                <Select defaultValue={c.status} onValueChange={(v) => handleUpdateStatus(c.contest_id, v)}>
                  <SelectTrigger className="bg-black/50 border-white/10 w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="voting">Voting</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);


export {
  OverviewTab, AnalyticsTab, PromoCodesTab, AIDesignTab,
  BeatsTab, MerchTab, CarsTab, UsersTab, LowStockTab,
  EmailCampaignsTab, VoiceTagTab, BlogAdminTab, LiveChatAdminTab,
  AffiliatesTab, AbandonedCartsTab, ABTestingTab, RewardsTab,
  BundlesTab, FlashSalesTab, GiftCardsTab, CarSubmissionsTab,
  ProducersAdminTab, TicketsAdminTab, SoundKitsAdminTab,
  AdvancedAnalyticsTab, SubscriptionsAdminTab, ContestsAdminTab
};
